import { yupResolver } from '@hookform/resolvers/yup';
import type { CreateCollectionRequest, EditCollectionRequest } from '@kimuno/api-sdk';
import { customSizes, spaces } from '@kimuno/theme';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material';
import type React from 'react';
import { useCallback } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import type { CollectionFormModalContextState } from '../../state/CollectionFormModalProvider';
import DialogActions from '../common/DialogActions';
import {
	CollectionDescription,
	CollectionSchoolStages,
	CollectionSubjects,
	CollectionTitle,
	CollectionVisibility,
	CustomColorSelection,
} from './components';
import type { CollectionFormValues } from './types';

interface CreateCollectionDialogProps {
	contextData: Pick<
		CollectionFormModalContextState,
		'isOpen' | 'hide' | 'save' | 'data' | 'collectionId' | 'canPublish'
	>;
}

// Rendered as the Dialog's child so the form state unmounts together with the
// fields: react-hook-form 7 keeps a remounted `Controller`'s first default under
// `shouldUnregister`, so a `useForm` that outlived the dialog would seed the next
// opening with the previous collection's subjects, school stage and colour.
const CollectionForm: React.FC<CreateCollectionDialogProps> = ({ contextData }) => {
	const { t } = useTranslation();
	const { hide, save, data, collectionId, canPublish } = contextData;

	const formValidator = yup.object({
		title: yup.string().required(t('This field is required')),
		description: yup.string(),
		subjects: yup.array().of(yup.string()),
		schoolStage: yup.string(),
		color: yup.string(),
		isPublic: yup.boolean(),
	});

	const {
		formState: { errors },
		handleSubmit,
		register,
		control,
	} = useForm<CollectionFormValues>({
		mode: 'onTouched',
		shouldUnregister: true,
		resolver: yupResolver(formValidator) as Resolver<CollectionFormValues>,
	});

	const handleOnSubmit = handleSubmit(
		useCallback(
			async (formData: CollectionFormValues) => {
				const payload = {
					title: formData.title,
					description: formData.description,
					subjects: formData.subjects,
					schoolStage: formData.schoolStage,
					color: formData.color,
					// Omitted entirely for users without the permission, so saving can never
					// change a collection's visibility from a form that never offered it.
					...(canPublish ? { isPublic: !!formData.isPublic } : {}),
				};

				if (collectionId) {
					return save(collectionId, payload as EditCollectionRequest);
				}
				return save(undefined, payload as CreateCollectionRequest);
			},
			[save, collectionId, canPublish]
		)
	);

	const DialogTitleSection = () => {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: spaces.lg,
				}}
			>
				<DialogTitle sx={{ padding: 0 }} variant="h5">
					{t('new collection')}
				</DialogTitle>
				<Box>
					<IconButton
						color="default"
						size="small"
						onClick={hide}
						data-testid="close-button"
						aria-label={t('Close')}
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</Box>
		);
	};

	return (
		<>
			<DialogTitleSection />
			<DialogContent
				sx={{
					padding: `${spaces.md} ${spaces.lg}`,
				}}
			>
				<Box component="form" data-testid="create-collection-dialog-form">
					<Stack direction="column" spacing={spaces.lg}>
						<CollectionTitle
							registration={register('title')}
							error={errors.title}
							initialValue={data?.title}
						/>
						<CollectionDescription
							registration={register('description')}
							error={errors.description}
							initialValue={data?.description}
						/>
						<CollectionSubjects control={control} initialValue={data?.subjects} />
						<CollectionSchoolStages control={control} initialValue={data?.schoolStage} />
						<CustomColorSelection control={control} initialValue={data?.color} />
						{canPublish && (
							<CollectionVisibility registration={register('isPublic')} initialValue={data?.isPublic} />
						)}
					</Stack>
				</Box>
			</DialogContent>
			<DialogActions
				primaryButton={{
					text: t('Save'),
					onClick: handleOnSubmit,
					type: 'submit',
				}}
				secondaryButton={{
					text: t('Cancel'),
					onClick: hide,
				}}
			/>
		</>
	);
};

const CreateCollectionDialog: React.FC<CreateCollectionDialogProps> = ({ contextData }) => {
	const { isOpen, hide } = contextData;

	return (
		<Dialog
			fullWidth
			aria-labelledby="create-collection-dialog"
			data-testid="create-collection-dialog"
			open={isOpen}
			onClose={hide}
			maxWidth={'lg'}
			sx={{
				'& .MuiDialog-paper': {
					width: customSizes.createCollectionDialog.width,
				},
			}}
		>
			<CollectionForm contextData={contextData} />
		</Dialog>
	);
};

export default CreateCollectionDialog;
